import { useState } from 'react';
import { useUrlState } from '@/hooks/useUrlState';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography, Tooltip, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Link } from '@liminal/web-ui/primitives/Link';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { Icon } from '@liminal/web-ui/icons/Icon';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { SearchInput } from '@liminal/web-ui/forms/SearchInput';
import { Select } from '@liminal/web-ui/forms/Select';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { spacing, borderRadius, fontSizes } from '@liminal/brand/scale';
import { webColors } from '@liminal/brand/semantic';
import { useNimMedia } from '@/hooks/useNimMedia';
import { useDebounce } from '@/hooks/useDebounce';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { formatOrderDate } from '@liminal/utils/date';
import { truncateMiddle } from '@liminal/utils/truncate';
import type { AdminNimMediaItem } from '@/api/types';

// A grid page, not a table page: 24 divides evenly into 2/3/4 columns so the
// last row is never a lone orphan tile at any breakpoint.
const PAGE_SIZE = 24;

// Nim's PostHog project. Traces are grouped by `$ai_session_id`, which nim sets
// to the conversation id (common/go/posthog: SessionID // conversationID), so a
// conversation id addresses a session page directly. There is exactly one
// PostHog project, so this is a constant rather than build config.
const POSTHOG_SESSION_URL = 'https://eu.posthog.com/project/107060/ai-observability/sessions';

const MediaGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: spacing.md,
});

// Square tiles keep the grid on a rhythm regardless of the source aspect
// ratio; `contain` shows the whole image rather than cropping a receipt or a
// screenshot, which is usually the part being inspected.
const Thumbnail = styled('img')({
  width: '100%',
  aspectRatio: '1 / 1',
  objectFit: 'contain',
  borderRadius: borderRadius.md,
  backgroundColor: webColors.background.tertiary,
  display: 'block',
  cursor: 'pointer',
});

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
];

const FILTER_DEFAULTS = { q: '', sort: 'newest' };

export function NimMediaPage() {
  const [{ q: query, sort }, setParams] = useUrlState(FILTER_DEFAULTS);
  const [lightbox, setLightbox] = useState<AdminNimMediaItem | null>(null);

  const pagination = useCursorPagination(PAGE_SIZE);
  const debouncedQuery = useDebounce(query, 300);

  const { data, isLoading, isError, refetch } = useNimMedia({
    query: debouncedQuery || undefined,
    pageSize: PAGE_SIZE,
    pageToken: pagination.pageToken,
    oldestFirst: sort === 'oldest',
  });

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const nextPageToken = data?.nextPageToken ?? '';

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <SearchInput
          value={query}
          onChange={(value) => {
            setParams({ q: value });
            pagination.reset();
          }}
          placeholder="Filter by email, name or user ID..."
          width={340}
        />
        <Select
          aria-label="Sort order"
          value={sort}
          onChange={(value) => {
            setParams({ sort: value });
            pagination.reset();
          }}
          options={SORT_OPTIONS}
          size="sm"
        />
        {totalCount > 0 && (
          <Typography variant="body2" color="text.secondary">
            {totalCount.toLocaleString()} images total
          </Typography>
        )}
      </Stack>

      {isError ? (
        <ErrorState
          message="Could not load media."
          onRetry={() => {
            void refetch();
          }}
        />
      ) : isLoading ? (
        <MediaGrid>
          {Array.from({ length: PAGE_SIZE }, (_, i) => (
            <Card key={i} padding="sm">
              <Skeleton height={200} />
            </Card>
          ))}
        </MediaGrid>
      ) : items.length === 0 ? (
        <EmptyState
          icon="image"
          title="No media found"
          subtitle={
            query
              ? 'No images match that user. Try a different email, name or user ID.'
              : 'No images have been uploaded to Nim yet.'
          }
        />
      ) : (
        <MediaGrid>
          {items.map((item) => (
            <Card key={item.messageId} padding="sm">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Tooltip title={item.content || 'No message text'} arrow>
                  <Thumbnail
                    src={item.signedUrl}
                    alt={item.content || 'Uploaded image'}
                    loading="lazy"
                    onClick={() => setLightbox(item)}
                  />
                </Tooltip>

                {item.userId ? (
                  <UserIdLink userId={item.userId}>
                    {item.email || truncateMiddle(item.userId)}
                  </UserIdLink>
                ) : (
                  <Typography variant="body2" color="text.disabled">
                    {'—'}
                  </Typography>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ fontSize: fontSizes.xs }}>
                  {item.createdAt ? formatOrderDate(item.createdAt) : '—'}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {item.conversationId && (
                    <>
                      <Link component={RouterLink} to={`/conversations/${item.conversationId}`}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: fontSizes.xs }}>
                          <Icon name="chat" size={14} />
                          Conversation
                        </Box>
                      </Link>
                      <Link external href={`${POSTHOG_SESSION_URL}/${item.conversationId}`}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: fontSizes.xs }}>
                          <Icon name="open-outline" size={14} />
                          PostHog
                        </Box>
                      </Link>
                    </>
                  )}
                </Box>
              </Box>
            </Card>
          ))}
        </MediaGrid>
      )}

      <CursorPagination
        firstRowNumber={pagination.firstRowNumber}
        count={items.length}
        hasPreviousPage={pagination.hasPreviousPage}
        hasNextPage={!!nextPageToken}
        onPreviousPage={() => pagination.goToPreviousPage()}
        onNextPage={() => pagination.goToNextPage(nextPageToken)}
        disabled={isLoading}
      />

      <Modal
        open={lightbox !== null}
        title={lightbox?.email || 'Image'}
        onClose={() => setLightbox(null)}
        dismissible
        width="lg"
      >
        {lightbox && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box
              component="img"
              src={lightbox.signedUrl}
              alt={lightbox.content || 'Uploaded image'}
              sx={{ width: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
            {lightbox.content && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {lightbox.content}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: fontSizes.xs }}>
              {lightbox.createdAt ? formatOrderDate(lightbox.createdAt) : '—'}
            </Typography>
          </Box>
        )}
      </Modal>
    </Stack>
  );
}
